// src/components/ReportModal.jsx
// Faz 1D Görev 4 — Reusable "Şikayet et" modal'ı.
//
// Kullanım:
//   const [rt, setRt] = useState(null);
//   <ReportModal open={!!rt} target={rt} onClose={() => setRt(null)} />
//
//   // aç:
//   setRt({ targetType: "clip", targetId: clip.id, clipId: clip.id });
//   setRt({ targetType: "comment", targetId: comment.id,
//           clipId: clip.id, commentId: comment.id });
//   setRt({ targetType: "reply", targetId: reply.id,
//           clipId, commentId, replyId: reply.id });
//
// Stilitistik: `BadgeDetailModal` + `ClipDetailModalDesktop` ile uyumlu inline
// overlay; ESC ile kapanır; submit sırasında butonlar disabled olur.

import React, { useEffect, useMemo, useRef, useState } from "react";
import useFocusTrap from "../hooks/useFocusTrap";
import {
  REPORT_DETAIL_MAX_LENGTH,
  REPORT_REASON_LIST,
  REPORT_REASON_LABEL_TR,
} from "../constants/reportSchema";
import { submitContentReport } from "../services/contentReports";
import { showToast } from "../ToastBoot";

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 5000,
  background: "rgba(0,0,0,.58)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 12,
};

const panelStyle: React.CSSProperties = {
  width: "min(92vw, 440px)",
  background: "#fff",
  color: "#111",
  borderRadius: 16,
  padding: "18px 18px 16px",
  boxShadow: "0 20px 50px rgba(0,0,0,.28)",
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const titleStyle: React.CSSProperties = {
  fontSize: 17,
  fontWeight: 700,
  margin: 0,
  lineHeight: 1.2,
};

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: "#444",
  textTransform: "uppercase",
  letterSpacing: 0.3,
  marginBottom: 6,
};

const selectStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #dcdfe6",
  background: "#fafbfc",
  fontSize: 14,
  color: "#111",
  outline: "none",
};

const textareaStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 88,
  maxHeight: 220,
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #dcdfe6",
  background: "#fafbfc",
  fontSize: 14,
  color: "#111",
  resize: "vertical",
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
};

const hintStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#777",
  display: "flex",
  justifyContent: "space-between",
};

const btnRow: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 8,
  marginTop: 4,
};

const btnBase: React.CSSProperties = {
  padding: "9px 14px",
  borderRadius: 10,
  border: "1px solid transparent",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  minWidth: 88,
};

const btnSecondary: React.CSSProperties = {
  ...btnBase,
  background: "#f3f4f6",
  color: "#222",
  borderColor: "#e0e2e7",
};

const btnDanger: React.CSSProperties = {
  ...btnBase,
  background: "#dc2626",
  color: "#fff",
};

const btnDisabled: React.CSSProperties = {
  opacity: 0.6,
  cursor: "not-allowed",
};

/**
 * Şikayet hedefi. Şekil İKİ ÇAĞRI YERİNDEN + bu dosyanın kendi JSDoc'undan
 * türetildi: `MyClips.tsx` `SikayetHedefi` (hepsi isteğe bağlı) ve
 * `ClipDetailModalDesktop.tsx` yerel state'i. `replyId` yalnız JSDoc'ta ve
 * `handleSubmit` gövdesinde geçiyor — o yüzden burada da isteğe bağlı.
 */
export type SikayetHedefi = {
  targetType?: string;
  targetId?: string;
  clipId?: string;
  commentId?: string;
  replyId?: string;
};

// Hedef tipine göre başlık
function titleFor(target: SikayetHedefi | null) {
  switch (target?.targetType) {
    case "comment":
      return "Yorumu şikayet et";
    case "reply":
      return "Yanıtı şikayet et";
    case "post":
      return "Gönderiyi şikayet et";
    case "clip":
    default:
      return "Klibi şikayet et";
  }
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {object|null} props.target  — { targetType, targetId, clipId?, commentId?, replyId? }
 * @param {() => void} props.onClose
 * @param {(res: {ok:boolean, reportId?:string, message?:string}) => void} [props.onSubmitted]
 */
type ReportModalProps = {
  open: boolean;
  target: SikayetHedefi | null;
  onClose: () => void;
  onSubmitted?: (res: { ok: boolean; reportId?: string; message?: string }) => void;
};

export default function ReportModal({ open, target, onClose, onSubmitted }: ReportModalProps) {
  // EMİR #98 Dalga-2 (a) — odak hapsi + beyan TEK KAYNAKTAN.
  // ⚠️ `ref` ile `{...katman}` AYNI elemanda: kancanın en sinsi kusuru
  // ("containerRef yanlış öğeyi gösteriyor") beyan ile hapsin ayrı
  // elemanlara yazılabilmesinden doğuyordu. Aynı yere yazılınca o kusur
  // kodlanamaz; ayırmak için birini SİLMEK gerekir, o da testte görünür.
  const hapisRef = useRef<HTMLDivElement | null>(null);

  const [reason, setReason] = useState<string>(REPORT_REASON_LIST[0]);
  const [detail, setDetail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // ⚠️ KORUMALI KAPANIŞ — codemod'un koruma dedektörü bunu KAÇIRDI (yalnız
  // dize karşılaştırmalı korumaları tanıyordu; buradaki `!submitting` boolean
  // olumsuzlama). Kaldırılan yerel Escape işleyicisi koşulsuz kapatmıyordu:
  // gönderim sürerken Escape modalı KAPATMAMALI. Koruma TAŞINDI, atılmadı.
  const escapeKapat = React.useCallback(() => {
    if (!submitting) onClose?.();
  }, [submitting, onClose]);
  const katman = useFocusTrap({ open, onClose: escapeKapat, containerRef: hapisRef });
  const firstFieldRef = useRef<HTMLSelectElement | null>(null);

  // Modal her açılışta formu sıfırla + ilk alana odaklan.
  useEffect(() => {
    if (!open) return;
    setReason(REPORT_REASON_LIST[0]);
    setDetail("");
    setSubmitting(false);
    const t = setTimeout(() => {
      try {
        firstFieldRef.current?.focus();
      } catch {}
    }, 0);
    return () => clearTimeout(t);
  }, [open]);

  // ESC → kapat
  const title = useMemo(() => titleFor(target), [target]);
  const charCount = detail.length;

  if (!open || !target) return null;

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    const res = await submitContentReport({
      targetType: target.targetType,
      targetId: target.targetId,
      clipId: target.clipId,
      commentId: target.commentId,
      replyId: target.replyId,
      reason,
      detail,
    });
    setSubmitting(false);
    if (res.ok) {
      showToast("Şikayetin iletildi. Ekibimiz inceleyecek.", { variant: "success" });
      try { onSubmitted?.(res); } catch {}
      onClose?.();
    } else {
      showToast(("message" in res && res.message) || "Şikayet gönderilemedi.", { variant: "error" });
      try { onSubmitted?.(res); } catch {}
    }
  };

  const stop = (e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation();

  return (
    <div ref={hapisRef} {...katman}
      style={overlayStyle}
      aria-label={title}
      onMouseDown={() => !submitting && onClose?.()}
    >
      <div style={panelStyle} onMouseDown={stop} onClick={stop}>
        <h3 style={titleStyle}>{title}</h3>

        <div>
          <div style={labelStyle}>Sebep</div>
          <select
            ref={firstFieldRef}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={submitting}
            style={selectStyle}
            aria-label="Şikayet sebebi"
          >
            {REPORT_REASON_LIST.map((r) => (
              <option key={r} value={r}>
                {REPORT_REASON_LABEL_TR[r] || r}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div style={labelStyle}>Açıklama (opsiyonel)</div>
          <textarea
            value={detail}
            onChange={(e) => setDetail(e.target.value.slice(0, REPORT_DETAIL_MAX_LENGTH))}
            disabled={submitting}
            maxLength={REPORT_DETAIL_MAX_LENGTH}
            placeholder="Detayları kısaca yaz (isteğe bağlı)…"
            style={textareaStyle}
          />
          <div style={hintStyle}>
            <span>Modlar incelerken görecek.</span>
            <span>{charCount}/{REPORT_DETAIL_MAX_LENGTH}</span>
          </div>
        </div>

        <div style={btnRow}>
          <button
            className="pressable"
            type="button"
            style={submitting ? { ...btnSecondary, ...btnDisabled } : btnSecondary}
            onClick={() => !submitting && onClose?.()}
            disabled={submitting}
          >
            İptal
          </button>
          <button
            className="pressable"
            type="button"
            style={submitting ? { ...btnDanger, ...btnDisabled } : btnDanger}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Gönderiliyor…" : "Gönder"}
          </button>
        </div>
      </div>
    </div>
  );
}
